<template>
	<transition name="slide-fade">
		<div ref="modal" class="bbox-modal-wrapper" v-if="show">
			<div class="bbox-modal-content" :class="{ auto, tiny, normal, big, huge }">
				<b-card :class="cardClasses">
					<!-- @slot Slot onde conteúdo do header é inserido -->
					<div
						v-if="$slots.header"
						class="bbox-modal-header position-relative"
						:class="{ [headerClasses]: !noPadding }"
					>
						<slot name="header" />
						<div
							tabindex="0"
							class="menu-modal position-absolute position-right-big font-size-big border-none bg-transparent"
							:class="{ 'margin-right-big': closeable }"
						>
							<hovering-menu
								v-if="menu && Array.isArray(menuOptions) && menuOptions.length"
								:visible="true"
								:options="menuOptions"
							></hovering-menu>
						</div>
						<button
							tabindex="0"
							class="close-modal position-absolute position-right-big font-size-xxbig border-none bg-transparent"
						>
							<b-icon class="ink-lighter-1" v-if="closeable" @click.native="hideModal()"
								><icon-clear
							/></b-icon>
						</button>
					</div>
					<!-- @slot Slot onde conteúdo do modal é inserido -->
					<div :class="{ [contentClasses]: !noPadding }">
						<button
							v-if="!$slots.header"
							tabindex="0"
							class="close-modal position-absolute position-right-normal font-size-xxbig border-none bg-transparent"
							style="top: 3%"
						>
							<b-icon class="ink-lighter-1" v-if="closeable" @click.native="hideModal()"
								><icon-clear
							/></b-icon>
						</button>
						<slot />
					</div>
					<!-- @slot Slot onde conteúdo do footer é inserido -->
					<div v-if="$slots.footer" class="bbox-modal-footer" :class="footerClasses">
						<slot name="footer" />
					</div>
				</b-card>
			</div>
			<div
				class="bbox-modal-bg bg-white-darker-4"
				@click="
					$emit('clickOutside');
					hideModal();
				"
			></div>
		</div>
	</transition>
</template>

<script>
import BCard from './Card.vue';
import BIcon from './Icon.vue';
import HoveringMenu from './HoveringMenu.vue';
import IconClear from '../icons/icon-clear.svg';

export default {
	name: 'b-modal',
	components: {
		BCard,
		BIcon,
		IconClear,
		HoveringMenu
	},
	props: {
		/**
		 * Se false modal não pode ser fechado pelos meios padrões,
		 * apenas utilizando os métodos disponíveis no modal
		 */
		closeable: {
			type: Boolean,
			default: true
		},
		/**
		 * Se true o conteúdo do modal tem tamanho variável, limitado pela props `tiny, normal, big, huge`
		 */
		auto: Boolean,
		/**
		 * Se true o modal possui menu`
		 */
		menu: Boolean,
		/*
                Opções do menu
                Formato do Array options
                [
                    {icon:'icon-create', text:'Editar', color: 'ink-lighter-1', method:edit, params: product},
                    {icon:'icon-delete', text:'Desativar', color: 'red-base', method:edit, params: product}
                ]
            */
		menuOptions: Array,
		/**
		 * Se true, o header e content do modal não tem padding ou bordas pré-aplicadas
		 */
		noPadding: Boolean,
		/**
		 * Se true modal tem `width=400`
		 */
		tiny: Boolean,
		/**
		 * Se true modal tem `width=600`. Valor padrão se nenhum tamanho é definido
		 */
		normal: Boolean,
		/**
		 * Se true modal tem `width=800`
		 */
		big: Boolean,
		/**
		 * Se true modal tem `width=960`
		 */
		huge: Boolean,
		/**
		 * Classes a serem passadas para o card interno do modal
		 */
		cardClasses: {
			type: String,
			default: 'padding-0 elevation-16dp'
		},
		/**
		 * Classes a serem passadas para o content do modal
		 */
		contentClasses: {
			type: String,
			default: 'padding-big'
		},
		/**
		 * Classes a serem passadas para o header do modal
		 */
		headerClasses: {
			type: String,
			default: 'custom-vertical-padding padding-big border-bottom border-white-darker-4'
		},
		/**
		 * Classes a serem passadas para o footer do modal
		 */
		footerClasses: {
			type: String,
			default: 'custom-vertical-padding padding-big border-top border-white-darker-4'
		},
		/**
		 * Se true, chama o método para abrir modal de
		 * confirmação antes de fechar.
		 */
		confirmationModal: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			show: false
		};
	},
	methods: {
		beforeOpen() {
			var bodyWidth = document.body.offsetWidth;
			document.querySelector('body, html').style.overflow = 'hidden';
			document.body.style.width = bodyWidth + 'px';
			document.addEventListener('keyup', this.closeListener);
		},
		beforeClose() {
			this.removeOverflowHidden();
			document.removeEventListener('keyup', this.closeListener);
			this.$emit('beforeClosed');
		},
		/**
		 * Abre o modal
		 * @public
		 */
		showModal() {
			this.beforeOpen();
			this.show = true;
			return new Promise(function(resolve) {
				setTimeout(function() {
					resolve();
				}, 200);
			});
		},
		/**
		 * Fecha o modal se closeable=true
		 * @public
		 */
		hideModal() {
			this.beforeClose();
			if (this.closeable && this.confirmationModal) {
				this.$emit('openConfirmationModal');
			} else if (this.closeable) {
				this.show = false;
				this.$emit('afterClosed');
				return new Promise(function(resolve) {
					setTimeout(function() {
						resolve();
					}, 200);
				});
			}
		},
		/**
		 * Força o fechamento do modal independentemente da propriedade closeable
		 * @public
		 */
		forceHideModal() {
			this.beforeClose();
			this.show = false;
		},
		/**
		 * Abre o modal se estiver fechado, e o fecha se estiver aberto
		 * @public
		 */
		toggleModal() {
			if (this.show) this.beforeClose();
			if (!this.show) this.beforeOpen();
			this.show = !this.show;
			return new Promise(function(resolve) {
				setTimeout(function() {
					resolve();
				}, 200);
			});
		},
		/**
		 * Retorna true se modal estiver aberto, false se estiver fechado
		 * @public
		 */
		isOpen() {
			return this.show;
		},
		closeListener(e) {
			if (e.keyCode === 27) this.hideModal();
		},
		removeOverflowHidden() {
			document.querySelector('body, html').style.overflow = 'auto';
			document.body.style.width = 'unset';
		}
	},
	mounted() {
		// var modal = this.$el;
		// modal.parentElement.removeChild(modal);
		// document.body.appendChild(modal);
	}
};
</script>

<style lang="scss" scoped>
$animationDuration: 0.2s;
.slide-fade-enter-active {
	transition: all $animationDuration ease;
}
.slide-fade-leave-active {
	transition: all $animationDuration ease;
}
.slide-fade-enter {
	opacity: 0;
}
.slide-fade-leave-to {
	opacity: 0;
}
.slide-fade-enter .bbox-modal-content {
	transform: translateY(-50%);
}
.slide-fade-leave-to .bbox-modal-content {
	transform: translateY(50%);
}
.custom-vertical-padding {
	padding-top: 2.4rem;
	padding-bottom: 2.4rem;
}
.menu-modal {
	top: calc(50% - 1.1rem);
	font-style: normal;
	display: block;
	cursor: pointer;
	padding: 0;
	line-height: 1;
	//color: $lightInk;
}
.close-modal {
	top: calc(50% - 1rem);
	font-style: normal;
	display: block;
	cursor: pointer;
	padding: 0;
	line-height: 1;
	z-index: 1;
	//color: $lightInk;
}
.close-modal:hover {
	//color: $ink;
}
div.bbox-modal-wrapper {
	position: fixed;
	padding-top: 2.5rem;
	padding-bottom: 2.5rem;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	overflow-y: auto;
	overflow-x: hidden;
	display: flex;
	box-sizing: border-box;
	z-index: 1000;
}
div.bbox-modal-bg {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	z-index: 1000;
	//background-color: $ink;
	opacity: 0.65;
	transform: translate(0, 0) !important;
}
div.bbox-modal-content {
	max-width: 95vw;
	margin: auto;
	width: auto;
	height: auto;
	z-index: 101;
	display: flex;
	justify-content: center;
	justify-items: center;
	align-content: center;
	align-items: center;
	z-index: 1001;
	transition: all $animationDuration ease;
	width: 600px;
	max-width: 95vw;
	& > div {
		width: 100%;
	}
	&.auto > div {
		width: auto;
	}
	&.tiny {
		width: 400px;
	}
	&.normal {
		width: 600px;
	}
	&.big {
		width: 800px;
	}
	&.huge {
		width: 960px;
	}
}
div.bbox-modal-content > *:not(.bbox-modal-bg) {
	position: relative;
	z-index: 101;
}
</style>

<docs>
    Caixa de diálogo

    As importações dos arquivos abaixo são necessárias para o funcionamento do componente:
    - spacing.scss
    - styling.scss
    - typography.scss

    ```js
    <b-button tertiary @clickOrEnter="$refs.modal.showModal()">Abrir modal</b-button>
    <b-modal auto ref="modal">
        Este é um modal!
    </b-modal>

    <b-button tertiary @clickOrEnter="$refs.tinyModal.showModal()">Tiny</b-button>
    <b-modal tiny ref="tinyModal">
        Este é um modal!
    </b-modal>

    <b-button tertiary @clickOrEnter="$refs.normalModal.showModal()">Normal</b-button>
    <b-modal normal ref="normalModal">
        Este é um modal!
    </b-modal>

    <b-button tertiary @clickOrEnter="$refs.bigModal.showModal()">Big</b-button>
    <b-modal big ref="bigModal">
        Este é um modal!
    </b-modal>

    <b-button tertiary @clickOrEnter="$refs.hugeModal.showModal()">Huge</b-button>
    <b-modal huge ref="hugeModal">
        Este é um modal!
    </b-modal>
    ```
</docs>
